import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../hooks/useLanguage';
import { useClinicName } from '../../hooks/useClinicName';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://elitmed.pro';
const OG_IMAGE = `${SITE_URL}/images/og-default.jpg`;

interface PageSEOProps {
  /** Заголовок без суффикса клиники */
  title: string;
  description: string;
  /** Полный URL страницы (без языкового префикса) */
  path?: string;
  /** URL картинки для OG (абсолютный) */
  image?: string;
  /** 'website' | 'article' | 'profile' */
  type?: string;
  /** Дополнительные теги внутрь Helmet */
  children?: React.ReactNode;
}

/**
 * Единый SEO-компонент.
 * Ставит title, description, canonical, og:*, twitter:*, hreflang.
 */
const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
  children,
}) => {
  const { language } = useLanguage();
  const clinicName = useClinicName();

  const fullTitle = `${title} | ${clinicName}`;
  const ogImage = image || OG_IMAGE;

  // Canonical всегда без языкового суффикса (язык — через Accept-Language / cookie)
  const canonical = `${SITE_URL}${path}`;

  // hreflang — один и тот же URL для всех языков (i18n на фронте)
  const langs: Array<{ lang: string; hreflang: string }> = [
    { lang: 'ru', hreflang: 'ru' },
    { lang: 'en', hreflang: 'en' },
    { lang: 'hy', hreflang: 'hy' },
    { lang: 'ru', hreflang: 'x-default' },
  ];

  return (
    <Helmet>
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* hreflang */}
      {langs.map(({ hreflang }) => (
        <link
          key={hreflang}
          rel="alternate"
          hrefLang={hreflang}
          href={canonical}
        />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={clinicName} />
      <meta property="og:locale" content={language === 'hy' ? 'hy_AM' : language === 'ru' ? 'ru_RU' : 'en_US'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {children}
    </Helmet>
  );
};

export default PageSEO;
